import { useState, useCallback } from 'react';
import { cartApi } from '../api';
import type { Cart } from '../types';
import { useTelegram } from './useTelegram';
import { pluralize } from '../utils/pluralize';

export const useCart = () => {
  const { tg } = useTelegram();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCart = useCallback(async (showNotification = true) => {
    try {
      setLoading(true);
      setError(null);
      const data = await cartApi.getCart();
      setCart(data.cart);

      // Show notification if items were removed
      if (showNotification && data.removed_items_count > 0) {
        const itemWord = pluralize(data.removed_items_count, 'товар', 'товара', 'товаров');
        tg?.showAlert(
          `${data.removed_items_count} ${itemWord} больше не доступен и был удален из корзины`
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch cart');
    } finally {
      setLoading(false);
    }
  }, [tg]);

  const addToCart = useCallback(async (productId: number, quantity: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      await cartApi.addItem(productId, quantity);
      await fetchCart(); // Refresh cart
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add to cart');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const updateCartItem = useCallback(async (itemId: number, quantity: number) => {
    try {
      setLoading(true);
      setError(null);
      await cartApi.updateItem(itemId, quantity);
      await fetchCart(); // Refresh cart
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update cart');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  const removeFromCart = useCallback(async (itemId: number) => {
    try {
      setLoading(true);
      setError(null);
      await cartApi.removeItem(itemId);
      await fetchCart(); // Refresh cart
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove from cart');
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchCart]);

  return {
    cart,
    loading,
    error,
    fetchCart,
    addToCart,
    updateCartItem,
    removeFromCart,
  };
};
